import React, { useState, useEffect, useCallback } from 'react';
import { subscriptionsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  FileText, Plus, Pencil, Trash2, X, Users, Building2,
  CreditCard, HardDrive, CheckCircle, XCircle,
} from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v || 0);

/* ============================================================
   PLAN FORM MODAL
   ============================================================ */
function PlanFormModal({ plan, onClose, onSave }) {
  const [form, setForm] = useState(plan || {
    name: '', slug: '', description: '', price_monthly: 0, price_yearly: 0,
    max_users: 5, max_companies: 1, max_invoices_month: 100, max_storage_mb: 500,
    features: [], is_active: true, trial_days: 14, sort_order: 0,
  });
  const [featureText, setFeatureText] = useState(
    (() => { try { return JSON.parse(plan?.features || '[]').join('\n'); } catch { return ''; } })()
  );
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = { ...form, features: featureText.split('\n').filter(Boolean) };
      if (!data.slug) data.slug = data.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      if (plan?.id) { await subscriptionsAPI.updatePlan(plan.id, data); toast.success('Plan updated'); }
      else { await subscriptionsAPI.createPlan(data); toast.success('Plan created'); }
      onSave();
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{plan?.id ? 'Edit Plan' : 'New Subscription Plan'}</h3>
          <button className="btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="sa-form-group">
                <label className="sa-form-label">Plan Name *</label>
                <input className="sa-input" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g. Professional" />
              </div>
              <div className="sa-form-group">
                <label className="sa-form-label">Slug</label>
                <input className="sa-input" value={form.slug} onChange={e => setForm({...form, slug: e.target.value})} placeholder="auto-generated" />
              </div>
            </div>
            <div className="sa-form-group">
              <label className="sa-form-label">Description</label>
              <input className="sa-input" value={form.description || ''} onChange={e => setForm({...form, description: e.target.value})} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="sa-form-group">
                <label className="sa-form-label">Monthly Price ($)</label>
                <input className="sa-input" type="number" step="0.01" min="0" value={form.price_monthly} onChange={e => setForm({...form, price_monthly: parseFloat(e.target.value) || 0})} />
              </div>
              <div className="sa-form-group">
                <label className="sa-form-label">Yearly Price ($)</label>
                <input className="sa-input" type="number" step="0.01" min="0" value={form.price_yearly} onChange={e => setForm({...form, price_yearly: parseFloat(e.target.value) || 0})} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
              <div className="sa-form-group">
                <label className="sa-form-label">Max Users</label>
                <input className="sa-input" type="number" min="1" value={form.max_users} onChange={e => setForm({...form, max_users: parseInt(e.target.value) || 1})} />
              </div>
              <div className="sa-form-group">
                <label className="sa-form-label">Max Companies</label>
                <input className="sa-input" type="number" min="1" value={form.max_companies} onChange={e => setForm({...form, max_companies: parseInt(e.target.value) || 1})} />
              </div>
              <div className="sa-form-group">
                <label className="sa-form-label">Invoices/Mo</label>
                <input className="sa-input" type="number" min="-1" value={form.max_invoices_month} onChange={e => setForm({...form, max_invoices_month: parseInt(e.target.value) || 100})} />
              </div>
              <div className="sa-form-group">
                <label className="sa-form-label">Storage (MB)</label>
                <input className="sa-input" type="number" min="0" value={form.max_storage_mb} onChange={e => setForm({...form, max_storage_mb: parseInt(e.target.value) || 500})} />
              </div>
            </div>
            <div className="sa-form-group">
              <label className="sa-form-label">Trial Days</label>
              <input className="sa-input" type="number" min="0" max="90" value={form.trial_days || 0} onChange={e => setForm({...form, trial_days: parseInt(e.target.value) || 0})} />
            </div>
            <div className="sa-form-group">
              <label className="sa-form-label">Features (one per line)</label>
              <textarea className="sa-input" rows={4} value={featureText} onChange={e => setFeatureText(e.target.value)} placeholder={"Full accounting\nInvoicing\nBasic reports"} />
            </div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: '#475569', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})} />
                Active
              </label>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save Plan'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ============================================================
   PLANS PAGE
   ============================================================ */
export default function SuperAdminPlans() {
  const [plans, setPlans] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([
        subscriptionsAPI.plans(),
        subscriptionsAPI.companies({}),
      ]);
      setPlans(p);
      setCompanies(c);
    } catch { /* */ }
  }, []);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  const handleDelete = async (plan) => {
    if (!window.confirm(`Delete plan "${plan.name}"? This cannot be undone.`)) return;
    try {
      await subscriptionsAPI.deletePlan(plan.id);
      toast.success('Plan deleted');
      load();
    } catch (err) { toast.error(err.message); }
  };

  if (loading) return <div className="spinner" style={{ margin: '80px auto' }} />;

  return (
    <div>
      {/* Toolbar */}
      <div className="sa-toolbar">
        <div className="sa-toolbar-left">
          <span className="sa-toolbar-title">{plans.length} Subscription Plans</span>
        </div>
        <button className="sa-btn sa-btn-primary" onClick={() => setShowForm({})}>
          <Plus size={14} /> New Plan
        </button>
      </div>

      {/* Plans grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
        {plans.map(plan => {
          const features = (() => { try { return JSON.parse(plan.features || '[]'); } catch { return []; } })();
          const companyCount = companies.filter(c => c.plan_name === plan.name).length;
          const yearlySave = plan.price_yearly > 0 && plan.price_monthly > 0
            ? Math.round((1 - plan.price_yearly / (plan.price_monthly * 12)) * 100)
            : 0;

          return (
            <div key={plan.id} style={{
              background: '#fff',
              border: `1px solid ${plan.is_active ? '#e2e8f0' : '#fde68a'}`,
              borderRadius: 10,
              overflow: 'hidden',
              opacity: plan.is_active ? 1 : 0.7,
            }}>
              {/* Plan header */}
              <div style={{
                padding: '18px 20px',
                borderBottom: '1px solid #f1f5f9',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{plan.name}</h3>
                  {plan.description && (
                    <p style={{ margin: '4px 0 0', fontSize: 12, color: '#64748b' }}>{plan.description}</p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className="sa-btn sa-btn-ghost sa-btn-sm" onClick={() => setShowForm(plan)} title="Edit">
                    <Pencil size={13} />
                  </button>
                  <button className="sa-btn sa-btn-ghost sa-btn-sm" onClick={() => handleDelete(plan)} title="Delete" style={{ color: '#dc2626' }}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>

              {/* Pricing */}
              <div style={{ padding: '16px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <span style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>{fmt(plan.price_monthly)}</span>
                  <span style={{ fontSize: 13, color: '#64748b' }}>/mo</span>
                </div>
                {plan.price_yearly > 0 && (
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    {fmt(plan.price_yearly)}/yr
                    {yearlySave > 0 && (
                      <span style={{ marginLeft: 6, color: '#16a34a', fontWeight: 700 }}>Save {yearlySave}%</span>
                    )}
                  </div>
                )}
              </div>

              {/* Limits */}
              <div style={{ padding: '0 20px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { icon: Users, text: `${plan.max_users} users` },
                  { icon: Building2, text: `${plan.max_companies} ${plan.max_companies === 1 ? 'company' : 'companies'}` },
                  { icon: CreditCard, text: `${plan.max_invoices_month === -1 ? 'Unlimited' : plan.max_invoices_month} invoices/mo` },
                  { icon: HardDrive, text: `${plan.max_storage_mb} MB storage` },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#475569' }}>
                    <item.icon size={13} style={{ color: '#94a3b8' }} />
                    {item.text}
                  </div>
                ))}
              </div>

              {/* Features */}
              {features.length > 0 && (
                <div style={{ padding: '0 20px 16px' }}>
                  {features.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#334155', marginBottom: 4 }}>
                      <CheckCircle size={12} style={{ color: '#16a34a', flexShrink: 0 }} />
                      {f}
                    </div>
                  ))}
                </div>
              )}

              {/* Footer */}
              <div style={{
                padding: '12px 20px',
                borderTop: '1px solid #f1f5f9',
                background: '#f8fafc',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <span style={{ fontSize: 11, color: '#64748b' }}>
                  {companyCount} {companyCount === 1 ? 'company' : 'companies'}
                </span>
                <span className={`sa-badge-status ${plan.is_active ? 'sa-badge-active' : 'sa-badge-expired'}`}>
                  {plan.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          );
        })}

        {/* Add new card */}
        <div
          onClick={() => setShowForm({})}
          style={{
            border: '2px dashed #e2e8f0',
            borderRadius: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 40,
            cursor: 'pointer',
            color: '#94a3b8',
            transition: 'all 0.15s',
            minHeight: 200,
          }}
          onMouseOver={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.color = '#3b82f6'; }}
          onMouseOut={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#94a3b8'; }}
        >
          <Plus size={24} style={{ marginBottom: 8 }} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>Create New Plan</span>
        </div>
      </div>

      {/* Modal */}
      {showForm !== null && (
        <PlanFormModal
          plan={showForm.id ? showForm : null}
          onClose={() => setShowForm(null)}
          onSave={() => { setShowForm(null); load(); }}
        />
      )}
    </div>
  );
}
